import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import {
  FCheckbox,
  FHAntdDateTimePicker,
  FHExcelInput,
  FHExcelSelect,
  FHTooltip,
} from '../../../../../base';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../../utils/colors';
import SaveIcon from '../../../../../svgs/SaveIcon';
import RemoveIcon from '../../../../../svgs/RemoveIcon';
import EditOutlinedIcon from '../../../../../svgs/EditOutlinedIcon';
import { disabledFutureDate, usDollarFormatter } from '../../../../../../helpers';
import { DATE_DISPLAY_FORMAT } from '../../../../../../constants';
import { PaymentTypeEnum } from '../constants';
import { usePayorResponsibilityContext } from './PayorResponsibilityTable';
import { convertToClaimDateFormat } from './utils';

import type { IClaimPayment, IClaimPolicy } from '../../../../../../utils/types';

const MAX_NUMERIC_VALUE = 1000000; // $1M

const FieldLabelMap = new Map([
  ['allowable', 'Allowable'],
  ['adjustments', 'Adjust'],
  ['deductibles', 'Deductibles'],
  ['co_insurance', 'Co-insurance'],
  ['co_pay', 'Co pay'],
  ['amount', 'Payment'],
]);

interface IPaymentRowProps {
  id?: number;
  sx?: any;
  claimId: number;
  isEditable: boolean;
  serviceLineId: number;
  isAllowableStored: boolean;
  payment: IClaimPayment;
  policies: IClaimPolicy[];
  isFirstRow?: boolean;
  isLastRow?: boolean;
  rowIndex?: number;
}

interface IPaymentEditRowProps {
  id?: number;
  sx?: any;
  claimId: number;
  serviceLineId: number;
  isAllowableStored: boolean;
  payment: IClaimPayment;
  isFirstRow?: boolean;
  policies: IClaimPolicy[];
  isLastRow?: boolean;
}

interface IPaymentState {
  policy_id: number | null;
  deductibles: number | null;
  co_insurance: number | null;
  co_pay: number | null;
  amount: number | null;
  allowable: number | null;
  adjustments: number | null;
  date_received: string | null;
  date_cleared: string | null;
  mode: string | null;
  is_amount_paid_to_subscriber: boolean;
  transaction_id: string | null;
}

interface IPaymentReadOnlyRowProps {
  id?: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  isEditable: boolean;
  payment: IClaimPayment;
  isAllowableStored: boolean;
  serviceLineId: number;
  rowIndex?: number;
  onEdit: () => void;
}

export function PaymentReadOnlyRow({
  isEditable,
  isFirstRow,
  isLastRow,
  payment,
  serviceLineId,
  isAllowableStored,
  rowIndex,
  onEdit,
}: IPaymentReadOnlyRowProps) {
  const { itemBeingModified, handleDeletePayment } = usePayorResponsibilityContext();

  const {
    allowable,
    adjustments,
    amount,
    deductibles,
    co_pay,
    co_insurance,
    date_received,
    date_cleared,
    transaction_id,
    is_amount_paid_to_subscriber,
    mode,
    policy,
  } = payment;

  return (
    <TableRow
      sx={{
        '&:hover': {
          td: {
            div: {
              button: {
                visibility: 'visible !important',
              },
            },
          },
        },
      }}
    >
      <TableCell
        sx={{
          position: 'relative',
          borderTopLeftRadius: isFirstRow ? '8px' : '0px',
          borderBottomLeftRadius: isLastRow ? '8px' : '0px',
          borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none',
        }}
      >
        <Box
          sx={{
            width: '30px',
            height: isFirstRow ? '118px' : '76px',
            borderBottomLeftRadius: '8px',
            border: `1px solid ${FORTA_BLUE.primary}`,
            borderTop: 'none',
            borderRight: 'none',
            position: 'absolute',
            top: isFirstRow ? -82 : -38,
            left: -38,
          }}
        ></Box>
        {isEditable && (
          <Box
            sx={{
              display: 'flex',
              visibility: 'hidden',
              background: 'white',
              zIndex: 100,
            }}
          >
            <IconButton
              sx={{ opacity: itemBeingModified ? 0 : 1 }}
              onClick={onEdit}
              disabled={Boolean(itemBeingModified)}
              data-testid={`editServiceRowButton_${isFirstRow}`}
            >
              <EditOutlinedIcon width={16} height={16} color={FORTA_BLUE.primary} />
            </IconButton>
            <IconButton
              sx={{ opacity: itemBeingModified ? 0 : 1 }}
              onClick={() => handleDeletePayment(serviceLineId, payment?.id)}
              disabled={Boolean(itemBeingModified)}
              data-testid={`deleteServiceRowButton_${isFirstRow}`}
            >
              <RemoveIcon width={18} height={18} />
            </IconButton>
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <Typography noWrap>
          {policy ? `${policy?.billing_payor?.label} - ${policy?.payor_responsibility}` : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`allowableSaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        {(allowable || allowable === 0) && (
          <Typography noWrap>{usDollarFormatter.format(allowable)}</Typography>
        )}
        {!allowable && allowable !== 0 && isAllowableStored ? (
          <Box mt={1}>
            <FHTooltip
              placement="top"
              title={'Allowable can only be entered once per service line'}
              arrow
              tooltipBoxShadow={`0px 4px 4px ${FORTA_BLUE.light2}`}
              tooltipbackground={FORTA_WHITE}
            >
              <InfoOutlinedIcon fontSize="small" style={{ color: FORTA_BLUE.primary }} />
            </FHTooltip>
          </Box>
        ) : (
          ''
        )}
      </TableCell>
      <TableCell
        data-testid={`adjustmentsSaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {adjustments || adjustments === 0 ? usDollarFormatter.format(adjustments) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`deductiblesSaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {deductibles || deductibles === 0 ? usDollarFormatter.format(deductibles) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`coinsuranceSaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {co_insurance || co_insurance === 0 ? usDollarFormatter.format(co_insurance) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`coPaySaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {co_pay || co_pay === 0 ? usDollarFormatter.format(co_pay) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`amountSaved_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {amount || amount === 0 ? usDollarFormatter.format(amount) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`dateReceived_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {date_received ? moment(date_received).format(DATE_DISPLAY_FORMAT) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`dateCleared_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {date_cleared ? moment(date_cleared).format(DATE_DISPLAY_FORMAT) : ''}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`PaymentPaidToSubscriberSaved__row_${rowIndex}_${is_amount_paid_to_subscriber}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>
          {is_amount_paid_to_subscriber ? (
            <CheckRoundedIcon sx={{ color: FORTA_BLUE.primary }} />
          ) : null}
        </Typography>
      </TableCell>
      <TableCell
        data-testid={`mode_row_${rowIndex}`}
        sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}
      >
        <Typography noWrap>{mode ? mode : ''}</Typography>
      </TableCell>
      <TableCell
        data-testid={`transactionId_row_${rowIndex}`}
        sx={{
          borderTopRightRadius: isFirstRow ? '8px' : '0px',
          borderBottomRightRadius: isLastRow ? '8px' : '0px',
          borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none',
        }}
      >
        <Typography noWrap>{transaction_id ? transaction_id : ''}</Typography>
      </TableCell>
    </TableRow>
  );
}

function PaymentEditRow({
  serviceLineId,
  payment,
  isAllowableStored,
  policies,
  isFirstRow = false,
  isLastRow = false,
}: IPaymentEditRowProps) {
  const { handleDeletePayment, handleSavePayment, setItemBeingModified } =
    usePayorResponsibilityContext();

  const [paymentState, setPaymentState] = useState<IPaymentState>(payment);
  const [numericInputErrors, setNumericInputErrors] = useState<{ [key: string]: boolean }>({
    allowable: false,
    adjustments: false,
    deductibles: false,
    co_insurance: false,
    co_pay: false,
    amount: false,
  });
  const paymentHasAllowable = Boolean(payment.allowable || payment.allowable === 0);

  const payorOptions: { value: number; label: string }[] = policies.map((policy) => ({
    value: policy.id,
    label: `${policy.billing_payor?.label} - ${policy.payor_responsibility}`,
  }));

  const updateDataInParent = (state: IPaymentState) => {
    const data = {
      ...state,
      date_received: paymentState.date_received
        ? convertToClaimDateFormat(paymentState.date_received)
        : null,
      date_cleared: paymentState.date_cleared
        ? convertToClaimDateFormat(paymentState.date_cleared)
        : null,
    };
    setItemBeingModified({ serviceLineId, data, paymentId: payment.id, action: 'save' });
  };

  const debouncedDataUpdater = useCallback(debounce(updateDataInParent, 500), []);

  useEffect(() => {
    initializeOrResetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  useEffect(() => {
    debouncedDataUpdater(paymentState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentState]);

  const initializeOrResetState = useCallback(() => {
    const data = {
      policy_id: payment.policy_id,
      deductibles: payment.deductibles,
      co_insurance: payment.co_insurance,
      co_pay: payment.co_pay,
      amount: payment.amount,
      allowable: payment.allowable,
      adjustments: payment.adjustments,
      date_received: payment.date_received
        ? moment(payment.date_received).format(DATE_DISPLAY_FORMAT)
        : '',
      date_cleared: payment.date_cleared
        ? moment(payment.date_cleared).format(DATE_DISPLAY_FORMAT)
        : '',
      mode: payment.mode,
      transaction_id: payment.transaction_id,
      is_amount_paid_to_subscriber: payment.is_amount_paid_to_subscriber,
    };
    setPaymentState(data);
  }, [payment]);

  const handleAmountUpdate = (field: string, value: string) => {
    if (!value) {
      setPaymentState((prev) => ({
        ...prev,
        [field]: null,
      }));
      return;
    }
    // Allow only 2 decimal places
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      return;
    }
    setPaymentState((prev) => ({
      ...prev,
      [field]: value ? value : null,
    }));
  };

  const handleFieldUpdate = (
    field: string,
    value: string | number | boolean,
    isBooleanType?: boolean,
  ) => {
    if (isBooleanType) {
      setPaymentState((prev) => ({ ...prev, [field]: value }));
      return;
    }
    setPaymentState((prev) => ({ ...prev, [field]: value ? value : null }));
  };

  const handleSave = async () => {
    let fieldWithError = '';
    for (const key in numericInputErrors) {
      if (numericInputErrors[key]) {
        fieldWithError = key;
      }
    }
    if (fieldWithError) {
      toast.error(
        `${FieldLabelMap.get(fieldWithError)} cannot be greater than ${MAX_NUMERIC_VALUE}`,
      );
      return;
    }
    const data = {
      ...paymentState,
      date_received: paymentState.date_received
        ? convertToClaimDateFormat(paymentState.date_received)
        : null,
      date_cleared: paymentState.date_cleared
        ? convertToClaimDateFormat(paymentState.date_cleared)
        : null,
    };
    handleSavePayment(serviceLineId, data, payment?.id);
  };

  return (
    <TableRow>
      <TableCell
        sx={{
          borderTopLeftRadius: isFirstRow ? '8px' : '0px',
          borderBottomLeftRadius: isLastRow ? '8px' : '0px',
          borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none',
          whiteSpace: 'nowrap',
          position: 'sticky',
          left: '0',
          background: 'white',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '30px',
            height: isFirstRow ? '118px' : '76px',
            borderBottomLeftRadius: '8px',
            border: `1px solid ${FORTA_BLUE.primary}`,
            borderTop: 'none',
            borderRight: 'none',
            position: 'absolute',
            top: isFirstRow ? -82 : -38,
            left: -38,
          }}
        ></Box>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <IconButton onClick={handleSave} data-testid="saveServiceRowButton">
            <SaveIcon width={16} height={16} color={FORTA_BLUE.primary} />
          </IconButton>
          <IconButton
            onClick={() => handleDeletePayment(serviceLineId, payment?.id)}
            data-testid={`deleteServiceRowButton_${isFirstRow}`}
          >
            <RemoveIcon width={18} height={18} />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelSelect
          value={paymentState.policy_id}
          data-testid="payorsDropdown"
          options={payorOptions}
          onUpdate={(value) => {
            if (value === paymentState.policy_id) return;
            handleFieldUpdate('policy_id', value);
          }}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        {((isAllowableStored && paymentHasAllowable) || !isAllowableStored) && (
          <FHExcelInput
            value={paymentState.allowable ?? ''}
            data-testid="allowableField"
            inputType="number"
            onNumericValueError={(value) => {
              setNumericInputErrors((prev) => ({
                ...prev,
                allowable: value,
              }));
            }}
            error={numericInputErrors.allowable}
            helperText={numericInputErrors.allowable ? `Max ${MAX_NUMERIC_VALUE}` : ''}
            onChange={(e) => handleAmountUpdate('allowable', e.target.value)}
          />
        )}
        {isAllowableStored && !paymentHasAllowable && (
          <Box mt={1}>
            <FHTooltip
              placement="top"
              title={'Allowable can only be entered once per service line'}
              arrow
              tooltipBoxShadow={`0px 4px 4px ${FORTA_BLUE.light2}`}
              tooltipbackground={FORTA_WHITE}
            >
              <InfoOutlinedIcon fontSize="small" style={{ color: FORTA_BLUE.primary }} />
            </FHTooltip>
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelInput
          value={paymentState.adjustments ?? ''}
          data-testid="adjustmentsField"
          inputType="number"
          onNumericValueError={(value) => {
            setNumericInputErrors((prev) => ({
              ...prev,
              adjustments: value,
            }));
          }}
          error={numericInputErrors.adjustments}
          helperText={numericInputErrors.adjustments ? `Max ${MAX_NUMERIC_VALUE}` : ''}
          onChange={(e) => handleAmountUpdate('adjustments', e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelInput
          value={paymentState.deductibles ?? ''}
          data-testid="deductiblesField"
          inputType="number"
          onNumericValueError={(value) => {
            setNumericInputErrors((prev) => ({
              ...prev,
              deductibles: value,
            }));
          }}
          error={numericInputErrors.deductibles}
          helperText={numericInputErrors.deductibles ? `Max ${MAX_NUMERIC_VALUE}` : ''}
          onChange={(e) => handleAmountUpdate('deductibles', e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelInput
          value={paymentState.co_insurance ?? ''}
          data-testid="co_insuranceField"
          inputType="number"
          onNumericValueError={(value) => {
            setNumericInputErrors((prev) => ({
              ...prev,
              co_insurance: value,
            }));
          }}
          error={numericInputErrors.co_insurance}
          helperText={numericInputErrors.co_insurance ? `Max ${MAX_NUMERIC_VALUE}` : ''}
          onChange={(e) => handleAmountUpdate('co_insurance', e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelInput
          value={paymentState.co_pay ?? ''}
          data-testid="co_payField"
          inputType="number"
          onNumericValueError={(value) => {
            setNumericInputErrors((prev) => ({
              ...prev,
              co_pay: value,
            }));
          }}
          error={numericInputErrors.co_pay}
          helperText={numericInputErrors.co_pay ? `Max ${MAX_NUMERIC_VALUE}` : ''}
          onChange={(e) => handleAmountUpdate('co_pay', e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelInput
          value={paymentState.amount ?? ''}
          data-testid="amountField"
          inputType="number"
          onNumericValueError={(value) => {
            setNumericInputErrors((prev) => ({
              ...prev,
              amount: value,
            }));
          }}
          error={numericInputErrors.amount}
          helperText={numericInputErrors.amount ? `Max ${MAX_NUMERIC_VALUE}` : ''}
          onChange={(e) => handleAmountUpdate('amount', e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHAntdDateTimePicker
          data-testid="date_receivedField"
          sx={{
            borderRadius: '4px',
            boxShadow: 'none',
            width: '105px',
            height: '28px',
            border: `1px solid ${FORTA_BLUE.light2}`,
            '&:hover': {
              border: `1px solid ${FORTA_BLUE.primary}`,
            },
            '&:focus-within': {
              border: `1px solid ${FORTA_BLUE.primary}`,
            },
          }}
          hideTimezoneDisplay
          iconWidth={14}
          iconHeight={14}
          format={DATE_DISPLAY_FORMAT}
          disabledDate={disabledFutureDate}
          value={paymentState.date_received ? moment(paymentState.date_received) : null}
          onChange={(value: moment.Moment | null) => {
            handleFieldUpdate('date_received', value ? value.format(DATE_DISPLAY_FORMAT) : '');
          }}
        />
      </TableCell>
      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHAntdDateTimePicker
          data-testid="date_clearedField"
          sx={{
            borderRadius: '4px',
            boxShadow: 'none',
            width: '105px',
            height: '28px',
            border: `1px solid ${FORTA_BLUE.light2}`,
            '&:hover': {
              border: `1px solid ${FORTA_BLUE.primary}`,
            },
            '&:focus-within': {
              border: `1px solid ${FORTA_BLUE.primary}`,
            },
          }}
          iconWidth={14}
          iconHeight={14}
          hideTimezoneDisplay
          disabledDate={disabledFutureDate}
          format={DATE_DISPLAY_FORMAT}
          value={paymentState.date_cleared ? moment(paymentState.date_cleared) : null}
          onChange={(value: moment.Moment | null) => {
            handleFieldUpdate('date_cleared', value ? value.format(DATE_DISPLAY_FORMAT) : '');
          }}
        />
      </TableCell>

      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FCheckbox
          iconStyles={{ boxShadow: 'none', width: '25px', height: '25px' }}
          data-testid="paidToSubscriberCheckbox"
          checked={paymentState.is_amount_paid_to_subscriber}
          onChange={(e) => {
            handleFieldUpdate('is_amount_paid_to_subscriber', e.target.checked, true);
          }}
          coloredBackground
        />
      </TableCell>

      <TableCell sx={{ borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none' }}>
        <FHExcelSelect
          value={paymentState.mode}
          data-testid="paymentTypeDropdown"
          options={Object.values(PaymentTypeEnum).map((value: string) => ({
            value,
            label: value,
          }))}
          onUpdate={(value) => handleFieldUpdate('mode', value)}
        />
      </TableCell>
      <TableCell
        sx={{
          borderTopRightRadius: isFirstRow ? '8px' : '0px',
          borderBottomRightRadius: isLastRow ? '8px' : '0px',
          borderBottom: isLastRow ? `1px solid ${FORTA_BLUE.light2}` : 'none',
        }}
      >
        <FHExcelInput
          sx={{ width: '130px' }}
          value={paymentState.transaction_id}
          data-testid="transaction_idField"
          onChange={(e) => handleFieldUpdate('transaction_id', e.target.value)}
        />
      </TableCell>
    </TableRow>
  );
}

function PaymentRow(props: IPaymentRowProps) {
  const { isEditable, serviceLineId, payment, rowIndex } = props;
  const { itemBeingModified, handleEditRow } = usePayorResponsibilityContext();
  const [editMode, setEditMode] = useState<boolean>(!props.payment.id);

  useEffect(() => {
    if (!itemBeingModified && editMode) {
      setEditMode(false);
    } else if (
      itemBeingModified?.serviceLineId === serviceLineId &&
      itemBeingModified.paymentId === payment.id &&
      itemBeingModified.action === 'edit'
    ) {
      setEditMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemBeingModified]);

  return editMode ? (
    <PaymentEditRow {...props} />
  ) : (
    <PaymentReadOnlyRow
      {...props}
      isEditable={isEditable}
      onEdit={() => handleEditRow(serviceLineId, payment.id)}
      rowIndex={rowIndex}
    />
  );
}

export default PaymentRow;
